import React, { useState, useEffect, ChangeEvent } from "react";
import { toast } from "react-toastify";

import Modal from "../../../../components/Reusable/Modal";
import { LabelInput, FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchDGCoinCategories,
  fetchDGCoinCategoryById,
  updateDGCoinCategory,
} from "../../../../features/auth/store/dgcoinCategorySlice";
import { fetchDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import { EditProps } from "../types";

export const EditDGCoinCategoryModal: React.FC<EditProps> = ({
  isOpen,
  id,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { dgcoinCategories, loading } = useAppSelector(
    (state) => state.dgcoinCategories
  );
  const { dgcoinProviders } = useAppSelector((state) => state.dgcoinProviders);

  const [formData, setFormData] = useState({
    provider_id: "",
    name: "",
    description: "",
    status: "on",
  });
  const [image, setImage] = useState<File | undefined>(undefined);

  useEffect(() => {
    if (isOpen && id) {
      dispatch(fetchDGCoinCategoryById(id));
      dispatch(fetchDGCoinProviders());
    }
  }, [dispatch, isOpen, id]);

  useEffect(() => {
    if (id) {
      const categoryToEdit = dgcoinCategories.find(
        (category) => category.id === id
      );
      if (categoryToEdit) {
        setFormData({
          provider_id: String(categoryToEdit.provider_id),
          name: categoryToEdit.name,
          description: categoryToEdit.description || "",
          status: categoryToEdit.status || "on",
        });
      }
    }
  }, [id, dgcoinCategories]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const formDataObj = new FormData();
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("name", formData.name);
    formDataObj.append("description", formData.description);
    formDataObj.append("status", formData.status);

    if (image) {
      formDataObj.append("image", image);
    }
    try {
      await dispatch(updateDGCoinCategory({ id, data: formDataObj })).unwrap();
      toast.success("DG Coin category updated successfully!");
      handleClose();
      dispatch(fetchDGCoinCategories({}));
    } catch (error: any) {
      toast.error(error?.message || "Failed to update DG Coin category");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setFormData({
      provider_id: "",
      name: "",
      description: "",
      status: "on",
    });
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">Edit DG Coin Category</p>
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...dgcoinProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          <LabelInput
            type="text"
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
          <LabelInput
            type="text"
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleInputChange}
          />
          <FloatingLabelSelect
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            placeholder="Status"
            options={[
              { value: "on", label: "Active" },
              { value: "off", label: "Inactive" },
            ]}
          />
          <div className="space-y-2">
            <label className="text-sm text-gray-500">Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500"
            />
            {image && (
              <div className="flex items-center gap-2">
                <span className="text-xs">{image.name}</span>
                <button
                  type="button"
                  className="text-red-500 text-xs"
                  onClick={removeImage}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-200"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="px-4 py-2"
              disabled={loading}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
